import { useState, useEffect } from 'react';
import { ArrowLeft, CalendarPlus, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface SubmitEventPageProps {
  onBack: () => void;
}

const CATEGORY_OPTIONS = [
  'Music', 'Arts & Theater', 'Food & Drink', 'Sports & Fitness',
  'Outdoors & Nature', 'Family Friendly', 'Festival', 'Community', 'Other',
];

export default function SubmitEventPage({ onBack }: SubmitEventPageProps) {
  useEffect(() => {
    const prevTitle = document.title;
    const descEl = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const prevDesc = descEl?.content ?? '';
    document.title = 'Submit an Event — Get Out Pitt';
    descEl?.setAttribute('content', 'Submit an event to be featured on Get Out Pitt. Our team reviews every submission before it appears on the map.');
    return () => {
      document.title = prevTitle;
      descEl?.setAttribute('content', prevDesc);
    };
  }, []);

  const [form, setForm] = useState({
    title: '',
    description: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    venue_name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    url: '',
    image_url: '',
    price: '',
    categories: [] as string[],
    submitter_name: '',
    submitter_email: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleCategory = (cat: string) => {
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(cat)
        ? f.categories.filter((c) => c !== cat)
        : [...f.categories, cat],
    }));
  };

  const buildDatetime = (date: string, time: string): string | null => {
    if (!date) return null;
    const t = time || '00:00';
    return new Date(`${date}T${t}`).toISOString();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const start_datetime = buildDatetime(form.start_date, form.start_time);
    const end_datetime = buildDatetime(form.end_date, form.end_time);

    const { error: dbError } = await supabase.from('event_requests').insert({
      title: form.title,
      description: form.description || null,
      start_datetime,
      end_datetime: form.end_date ? end_datetime : null,
      venue_name: form.venue_name || null,
      address: form.address || null,
      city: form.city || null,
      state: form.state || null,
      zip_code: form.zip_code || null,
      url: form.url || null,
      image_url: form.image_url || null,
      price: form.price || null,
      categories: form.categories.length > 0 ? form.categories : null,
      submitter_name: form.submitter_name || null,
      submitter_email: form.submitter_email || null,
    });

    setSubmitting(false);
    if (dbError) {
      setError('Something went wrong submitting your event. Please try again.');
      return;
    }
    setSubmitted(true);
  };

  const inputCls = 'w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400/60 transition-all';
  const labelCls = 'block text-xs font-semibold text-slate-700 mb-1.5';

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-zinc-950 text-white z-20 border-b border-zinc-800/80">
        <div className="px-4 sm:px-6 py-3 flex items-center gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <span className="text-[17px] font-bold tracking-tight leading-none">
            Get Out <span className="text-amber-400">Pitt</span>
          </span>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-10">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <CalendarPlus className="w-5 h-5 text-amber-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900">Submit an Event</h1>
          </div>
          <p className="text-slate-500 leading-relaxed">
            Know about an event that should be on our map? Fill out the form below and our team will review it before publishing.
          </p>
        </div>

        {submitted ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-6 h-6 text-emerald-500" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Submission Received</h2>
            <p className="text-sm text-slate-500 mb-6">
              Thanks for contributing! Our team will review your event and publish it if it meets our guidelines. You can check back soon.
            </p>
            <button
              onClick={onBack}
              className="px-5 py-2 bg-zinc-900 text-white text-sm font-medium rounded-lg hover:bg-zinc-800 transition-colors"
            >
              Back to Events
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              {/* Event details */}
              <div>
                <h3 className="text-sm font-bold text-slate-800 mb-4 pb-2 border-b border-slate-100">Event Details</h3>

                <div className="flex flex-col gap-5">
                  <div>
                    <label className={labelCls}>Event title <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      required
                      maxLength={300}
                      value={form.title}
                      onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                      placeholder="Pittsburgh Jazz Festival"
                      className={inputCls}
                    />
                  </div>

                  <div>
                    <label className={labelCls}>Description</label>
                    <textarea
                      rows={4}
                      maxLength={5000}
                      value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      placeholder="Tell us what this event is about..."
                      className={`${inputCls} resize-none`}
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Start date</label>
                      <input
                        type="date"
                        value={form.start_date}
                        onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Start time</label>
                      <input
                        type="time"
                        value={form.start_time}
                        onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                        className={inputCls}
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>End date</label>
                      <input
                        type="date"
                        value={form.end_date}
                        onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>End time</label>
                      <input
                        type="time"
                        value={form.end_time}
                        onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
                        className={inputCls}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>Categories</label>
                    <div className="flex flex-wrap gap-2">
                      {CATEGORY_OPTIONS.map((cat) => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => toggleCategory(cat)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                            form.categories.includes(cat)
                              ? 'bg-amber-500 text-zinc-950'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Price</label>
                      <input
                        type="text"
                        maxLength={100}
                        value={form.price}
                        onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                        placeholder="Free, $20, $10–$25"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Event / ticket URL</label>
                      <input
                        type="url"
                        value={form.url}
                        onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                        placeholder="https://..."
                        className={inputCls}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>Image URL</label>
                    <input
                      type="url"
                      value={form.image_url}
                      onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))}
                      placeholder="https://example.com/image.jpg"
                      className={inputCls}
                    />
                  </div>
                </div>
              </div>

              {/* Location */}
              <div>
                <h3 className="text-sm font-bold text-slate-800 mb-4 pb-2 border-b border-slate-100">Location</h3>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Venue name</label>
                    <input
                      type="text"
                      maxLength={200}
                      value={form.venue_name}
                      onChange={(e) => setForm((f) => ({ ...f, venue_name: e.target.value }))}
                      placeholder="Stage AE, PNC Park, Schenley Park..."
                      className={inputCls}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Address</label>
                    <input
                      type="text"
                      maxLength={300}
                      value={form.address}
                      onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                      placeholder="400 Art Rooney Ave"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>City</label>
                    <input
                      type="text"
                      maxLength={100}
                      value={form.city}
                      onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                      placeholder="Pittsburgh"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>State</label>
                    <input
                      type="text"
                      maxLength={20}
                      value={form.state}
                      onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                      placeholder="PA"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>ZIP code</label>
                    <input
                      type="text"
                      maxLength={20}
                      value={form.zip_code}
                      onChange={(e) => setForm((f) => ({ ...f, zip_code: e.target.value }))}
                      placeholder="15212"
                      className={inputCls}
                    />
                  </div>
                </div>
              </div>

              {/* Submitter info */}
              <div>
                <h3 className="text-sm font-bold text-slate-800 mb-4 pb-2 border-b border-slate-100">Your Information</h3>
                <p className="text-xs text-slate-400 mb-4">Optional — but helps us contact you if we have questions about the event.</p>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls}>Your name</label>
                    <input
                      type="text"
                      maxLength={120}
                      value={form.submitter_name}
                      onChange={(e) => setForm((f) => ({ ...f, submitter_name: e.target.value }))}
                      placeholder="Jane Doe"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Your email</label>
                    <input
                      type="email"
                      maxLength={254}
                      value={form.submitter_email}
                      onChange={(e) => setForm((f) => ({ ...f, submitter_email: e.target.value }))}
                      placeholder="you@example.com"
                      className={inputCls}
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="flex items-center justify-center gap-2 px-5 py-2.5 bg-zinc-900 text-white text-sm font-semibold rounded-lg hover:bg-zinc-800 disabled:opacity-60 transition-all"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
                ) : (
                  <CalendarPlus className="w-4 h-4" />
                )}
                {submitting ? 'Submitting...' : 'Submit Event'}
              </button>

              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}
            </form>
          </div>
        )}
      </main>

      <footer className="border-t border-slate-200 py-6 text-center text-xs text-slate-400">
        <p>&copy; {new Date().getFullYear()} Get Out Pitt. All rights reserved.</p>
      </footer>
    </div>
  );
}
